/**
 * What a file preview window shows: a reference to the file, never a ready-made
 * URL (cdb_webos#1619). A blob URL is gone after a reload; a reference lets a
 * restored or deep-linked window load its file again. Who may see the file is
 * decided by cdb_tqs when it is loaded, not here.
 *
 * Window params carry the reference flat (`fileRef` names the kind), so deep
 * links and saved window layouts keep it as they keep any other param.
 */
import type { WindowParams } from '@/types/window'

export type FileRef =
  | { kind: 'knowledge-area'; documentId: string; knowledgeArea: string; fileName: string }
  | { kind: 'personal'; fileId: string; fileName: string }
  | { kind: 'attachment'; ticketId: string; attachmentId: string; fileName: string }
  /** An expert interview recording (`expert-interview-audio-service`). */
  | { kind: 'recording'; audioId: string; extension: string; fileName: string }

export type FileRefKind = FileRef['kind']

/** The window params that carry a reference. */
export function fileRefToParams(ref: FileRef): WindowParams {
  const { kind, ...fields } = ref
  return { fileRef: kind, ...fields }
}

function text(params: WindowParams, key: string): string {
  const value = params[key]
  return typeof value === 'string' ? value : ''
}

/**
 * The reference in a window's params, or null. Windows saved before #1619
 * carry `documentId` + `knowledgeArea` without `fileRef`; they are a
 * Knowledge Area document. `fileName` may then be missing — `resolveFileName`
 * asks the backend.
 */
export function fileRefFromParams(params: WindowParams | undefined): FileRef | null {
  if (!params) return null
  const fileName = text(params, 'fileName')
  switch (params.fileRef) {
    case 'personal': {
      const fileId = text(params, 'fileId')
      return fileId ? { kind: 'personal', fileId, fileName } : null
    }
    case 'attachment': {
      const ticketId = text(params, 'ticketId')
      const attachmentId = text(params, 'attachmentId')
      return ticketId && attachmentId ? { kind: 'attachment', ticketId, attachmentId, fileName } : null
    }
    case 'recording': {
      const audioId = text(params, 'audioId')
      const extension = text(params, 'extension')
      return audioId && extension ? { kind: 'recording', audioId, extension, fileName } : null
    }
    default: {
      const documentId = text(params, 'documentId')
      const knowledgeArea = text(params, 'knowledgeArea')
      return documentId && knowledgeArea ? { kind: 'knowledge-area', documentId, knowledgeArea, fileName } : null
    }
  }
}
